//=============================================================================
// Promotion Stacking - Create
//=============================================================================

import React, {
  useState,
  useEffect,
  useContext,
} from "react";

import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import { styles } from "../Promotion/Criteria/styles";
import StackingPromotion from "apis/StackingPromotion/StackingPromotion";
import Context from "context/context";

// Material UI
import { withStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import IconButton from "@material-ui/core/IconButton";
import Checkbox from "@material-ui/core/Checkbox";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";
import Box from "@material-ui/core/Box";

// Icons
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import LayersIcon from "@material-ui/icons/Layers";
import SearchIcon from "@material-ui/icons/Search";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";

import { useTranslation } from "react-i18next";


//=============================================================================
// Rounded TextField
//=============================================================================

const CssTextField = withStyles({
  root: {
    "& .MuiFormLabel-root": { color: "#707070" },
    "& .MuiInputBase-input": { color: "#1a1a1a" },
    "& .MuiOutlinedInput-root": {
      borderRadius: 24,
      "& fieldset": { borderColor: "#D3D3D3" },
      "&:hover fieldset": { borderColor: "#9E9E9E" },
      "&.Mui-focused fieldset": { borderColor: "#305698" },
    },
    "& .MuiOutlinedInput-input": { padding: "14px 18px" },
    "& .MuiSelect-select": { padding: "14px 18px" },
  },
})(TextField);


//=============================================================================
// Category
//=============================================================================

const CATEGORY_OPTIONS = [
  { label: "Order id", value: "order_id", apiType: "Order_id" },
  { label: "Order line number", value: "order_line_number", apiType: "Order_line_number" },
];


//=============================================================================
// Component
//=============================================================================

export default function StackingPromoCreate() {
  const { dispatch } = useContext(Context);

  const history = useHistory();
  const classes = styles();
  const { t } = useTranslation();

  const { getAvailablePromotions, createStack } = StackingPromotion();


  //===========================================================================
  // Form state
  //===========================================================================

  const [promotionName, setPromotionName] = useState("");
  const [promotionDescription, setPromotionDescription] = useState("");
  const [category, setCategory] = useState("");
  const [isActive, setIsActive] = useState(true);


  //===========================================================================
  // Existing Promotion_Rule records
  //
  // Each item: { promotion_id, promotion_name, promotion_description, selected }
  //===========================================================================

  const [promotionsList, setPromotionsList] = useState([]);
  const [loadingPromotions, setLoadingPromotions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchValue, setSearchValue] = useState("");


  //===========================================================================
  // Breadcrumb
  //===========================================================================

  useEffect(() => {
    dispatch({
      type: "SET_PATHNAME",
      payload: ["HOME", "ADMIN", "Promotion Management", "Criteria", "Create"],
    });
  }, [dispatch]);


  //===========================================================================
  // Category change
  //===========================================================================

  const handleCategoryChange = async (event) => {
    const selectedCategory = event.target.value;

    setCategory(selectedCategory);
    setPromotionsList([]);
    setSearchValue("");

    if (!selectedCategory) {
      return;
    }

    await loadPromotions(selectedCategory);
  };


  //===========================================================================
  // Get existing, unattached Promotion_Rule records for this category
  //===========================================================================

  const loadPromotions = async (selectedCategory) => {
    try {
      setLoadingPromotions(true);

      const selectedOption = CATEGORY_OPTIONS.find(
        (item) => item.value === selectedCategory
      );

      if (!selectedOption) {
        return;
      }

      const response = await getAvailablePromotions({ type: selectedOption.apiType });

      if (response?.success) {
        const records = response?.data || [];

        setPromotionsList(
          records.map((item) => ({
            ...item,
            selected: false,
          }))
        );
      } else {
        setPromotionsList([]);
      }
    } catch (error) {
      console.error("loadPromotions error:", error);
      toast.error("Unable to retrieve promotions", { toastId: "load-promotions-error" });
      setPromotionsList([]);
    } finally {
      setLoadingPromotions(false);
    }
  };


  //===========================================================================
  // Toggle existing promotion — keyed by promotion_id (unique),
  // NOT promotion_name (two promotions can share the same name)
  //===========================================================================

  const togglePromotionSelected = (promotionId) => {
    setPromotionsList((list) =>
      list.map((promotion) =>
        promotion.promotion_id === promotionId
          ? { ...promotion, selected: !promotion.selected }
          : promotion
      )
    );
  };


  //===========================================================================
  // Select all (visible/filtered rows only)
  //===========================================================================

  const handleSelectAll = () => {
    const visiblePromotions = getFilteredPromotions();

    const allSelected = visiblePromotions.every((promotion) => promotion.selected);

    const visibleIds = new Set(visiblePromotions.map((item) => item.promotion_id));

    setPromotionsList((list) =>
      list.map((promotion) =>
        visibleIds.has(promotion.promotion_id)
          ? { ...promotion, selected: !allSelected }
          : promotion
      )
    );
  };


  //===========================================================================
  // Filter
  //===========================================================================

  const getFilteredPromotions = () => {
    if (!searchValue.trim()) {
      return promotionsList;
    }

    const search = searchValue.toLowerCase();

    return promotionsList.filter((promotion) =>
      String(promotion?.promotion_name || "").toLowerCase().includes(search) ||
      String(promotion?.promotion_description || "").toLowerCase().includes(search)
    );
  };


  //===========================================================================
  // Selected promotions
  //===========================================================================

  const selectedPromotions = promotionsList.filter((promotion) => promotion.selected);


  //===========================================================================
  // Submit
  //===========================================================================

  const handleSubmit = async () => {

    if (!category) {
      toast.error("Select a category", { toastId: "category-required" });
      return;
    }

    if (!promotionName.trim()) {
      toast.error("Enter Promotion Name", { toastId: "promotion-name-required" });
      return;
    }

    if (!promotionDescription.trim()) {
      toast.error("Enter Promotion description", { toastId: "promotion-description-required" });
      return;
    }

    const alreadyExists = promotionsList.some(
      (promotion) =>
        String(promotion.promotion_name || "").toLowerCase() ===
        promotionName.trim().toLowerCase()
    );

    if (alreadyExists) {
      toast.error(
        "This Promotion Name already exists in the list. Select it from the list instead.",
        { toastId: "promotion-already-exists" }
      );
      return;
    }

    if (selectedPromotions.length < 2) {
      toast.error("Select at least two existing promotion to create a stack", {
        toastId: "promotion-selection-required",
      });
      return;
    }

    try {
      setSaving(true);

      const selectedCategory = CATEGORY_OPTIONS.find((item) => item.value === category);

      const payload = {
        type: selectedCategory.apiType,
        isActive,
        promotionName: promotionName.trim(),
        promotionDescription: promotionDescription.trim(),
        existingPromotionIds: selectedPromotions.map((promotion) => promotion.promotion_id),
      };

      const response = await createStack(payload);

      if (response?.conflict) {
        toast.error(
          response?.message ||
            "One or more promotions are already assigned to another stack",
          { toastId: "stack-conflict" }
        );
        return;
      }
      
      if (response?.success) {
        toast.success(
          response?.message || "Promotion stacking created successfully",
          { toastId: "stack-created" }
        );
        history.goBack();
        return;
      }
      
      toast.error(
        response?.message || "Failed to create promotion stacking",
        { toastId: "stack-create-failed" }
      );
    } catch (error) {
      console.error("createStack error:", error);
      toast.error("Failed to create promotion stacking", { toastId: "stack-create-error" });
    } finally {
      setSaving(false);
    }
  };


  //===========================================================================
  // Current visible list
  //===========================================================================

  const filteredPromotions = getFilteredPromotions();

  const allVisibleSelected =
    filteredPromotions.length > 0 &&
    filteredPromotions.every((promotion) => promotion.selected);

  const categoryLabel =
    CATEGORY_OPTIONS.find((item) => item.value === category)?.label || "";


  //===========================================================================
  // UI
  //===========================================================================

  return (
    <React.Fragment>
      <div className={classes.mainDiv} style={{ paddingBottom: 30 }}>

        {/* Header */}
        <Grid container alignItems="center" style={{ padding: "12px 18px 8px" }}>
          <Grid item>
            <IconButton onClick={() => history.goBack()} size="small">
              <ArrowBackIcon style={{ color: "#305698" }} />
            </IconButton>
          </Grid>
          <Grid item>
            <Typography style={{ fontSize: 20, fontWeight: 600, color: "#202020" }}>
              {t("Create Promotion Stack")}
            </Typography>
          </Grid>
        </Grid>


        {/* Main form card */}
        <Paper
          elevation={0}
          style={{ margin: "8px 18px 0", border: "1px solid #E1E5EA", borderRadius: 18, padding: 20 }}
        >
          <Grid container spacing={2}>

            <Grid item xs={12} sm={6} md={3}>
              <CssTextField
                id="PromotionName"
                label={t("Promotion Name")}
                variant="outlined"
                fullWidth
                required
                value={promotionName}
                onChange={(event) => setPromotionName(event.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <CssTextField
                id="PromotionDescription"
                label={t("Promotion description")}
                variant="outlined"
                fullWidth
                required
                value={promotionDescription}
                onChange={(event) => setPromotionDescription(event.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <CssTextField
                id="Category"
                label={t("Category")}
                variant="outlined"
                select
                fullWidth
                required
                value={category}
                onChange={handleCategoryChange}
              >
                {CATEGORY_OPTIONS.map((option) => (
                  <MenuItem value={option.value} key={option.value}>
                    {t(option.label)}
                  </MenuItem>
                ))}
              </CssTextField>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <CssTextField
                id="Status"
                label={t("Status")}
                variant="outlined"
                select
                fullWidth
                required
                value={isActive}
                onChange={(event) =>
                  setIsActive(event.target.value === "true" || event.target.value === true)
                }
              >
                <MenuItem value={true}>Active</MenuItem>
                <MenuItem value={false}>Inactive</MenuItem>
              </CssTextField>
            </Grid>

          </Grid>

          {category && (
            <Box
              style={{
                marginTop: 18,
                padding: "12px 16px",
                borderRadius: 12,
                background: "#F4F8FC",
                border: "1px solid #D9E6F4",
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <InfoOutlinedIcon style={{ color: "#305698", fontSize: 20 }} />
              <Typography style={{ fontSize: 13, color: "#4D5B6A" }}>
                Select existing {categoryLabel} promotions below. The selected promotions
                will be assigned to the same stacking group as the new promotion.
              </Typography>
            </Box>
          )}
        </Paper>


        {/* Promotions section */}
        {category && (
          <Paper
            elevation={0}
            style={{ margin: "18px 18px 0", border: "1px solid #E1E5EA", borderRadius: 18, overflow: "hidden" }}
          >
            <Box style={{ padding: "18px 22px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <Box style={{ width: 40, height: 40, borderRadius: 12, background: "#EAF2FB", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <LayersIcon style={{ color: "#305698" }} />
                </Box>
                <Box>
                  <Typography style={{ fontSize: 17, fontWeight: 600, color: "#202020" }}>
                    Promotions to stack
                  </Typography>
                  <Typography style={{ fontSize: 13, color: "#777", marginTop: 3 }}>
                    {categoryLabel} promotions from Promotion Rule
                  </Typography>
                </Box>
              </Box>

              <Chip
                label={`${selectedPromotions.length} selected`}
                style={{ background: "#EAF2FB", color: "#305698", fontWeight: 500 }}
              />
            </Box>

            <Divider />

            <Box style={{ padding: "14px 22px", display: "flex", alignItems: "center", gap: 15 }}>
              <CssTextField
                value={searchValue}
                onChange={(event) => setSearchValue(event.target.value)}
                placeholder="Search promotion"
                variant="outlined"
                size="small"
                style={{ maxWidth: 350 }}
                InputProps={{
                  startAdornment: <SearchIcon style={{ color: "#888", marginRight: 8, fontSize: 20 }} />,
                }}
              />

              <Button
                variant="text"
                color="primary"
                onClick={handleSelectAll}
                disabled={loadingPromotions || filteredPromotions.length === 0}
              >
                {allVisibleSelected ? "Unselect all" : "Select all"}
              </Button>
            </Box>

            <Divider />

            {loadingPromotions && (
              <Box style={{ padding: 40, textAlign: "center" }}>
                <CircularProgress size={28} style={{ color: "#305698" }} />
                <Typography style={{ marginTop: 12, fontSize: 13, color: "#777" }}>
                  Loading promotions...
                </Typography>
              </Box>
            )}

            {!loadingPromotions && filteredPromotions.length === 0 && (
              <Box style={{ padding: 40, textAlign: "center" }}>
                <Typography style={{ fontSize: 14, color: "#777" }}>
                  No available promotions found for {categoryLabel}.
                </Typography>
              </Box>
            )}

            {!loadingPromotions && filteredPromotions.length > 0 && (
              <Box style={{ maxHeight: 400, overflowY: "auto" }}>
                {filteredPromotions.map((promotion, index) => (
                  <Box
                    key={promotion.promotion_id}
                    onClick={() => togglePromotionSelected(promotion.promotion_id)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "13px 22px",
                      cursor: "pointer",
                      background: promotion.selected ? "#F5F9FD" : "#FFFFFF",
                      borderBottom: index !== filteredPromotions.length - 1 ? "1px solid #EEEEEE" : "none",
                    }}
                  >
                    <Checkbox
                      checked={!!promotion.selected}
                      onChange={() => togglePromotionSelected(promotion.promotion_id)}
                      color="primary"
                      onClick={(event) => event.stopPropagation()}
                    />

                    <Box style={{ flex: 1 }}>
                      <Typography style={{ fontSize: 14, fontWeight: 600, color: "#252525" }}>
                        {promotion.promotion_name}
                      </Typography>
                      <Typography style={{ fontSize: 13, color: "#777", marginTop: 3 }}>
                        {promotion.promotion_description || "-"}
                      </Typography>
                    </Box>

                    {promotion.selected && (
                      <CheckCircleOutlineIcon style={{ color: "#305698", fontSize: 20 }} />
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        )}


        {/* Stack preview */}
        {category && promotionName && selectedPromotions.length > 0 && (
          <Paper
            elevation={0}
            style={{ margin: "18px 18px 0", border: "1px solid #D8E4F0", background: "#F8FBFE", borderRadius: 18, padding: 20 }}
          >
            <Typography style={{ fontSize: 15, fontWeight: 600, color: "#305698", marginBottom: 10 }}>
              Stack Preview
            </Typography>

            <Box style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
              {selectedPromotions.map((promotion) => (
                <Chip
                  key={promotion.promotion_id}
                  label={promotion.promotion_name || promotion.promotion_description || promotion.promotion_id}
                  size="small"
                  style={{ background: "#FFFFFF", border: "1px solid #C9D9E8" }}
                />
              ))}

              <Typography style={{ fontWeight: 600, color: "#777" }}>+</Typography>

              <Chip
                label={promotionName}
                size="small"
                style={{ background: "#305698", color: "#FFFFFF", fontWeight: 600 }}
              />
            </Box>

            <Typography style={{ marginTop: 12, fontSize: 13, color: "#666" }}>
              The new promotion <strong>{promotionName}</strong> will be created in Promotion Rule
              and all selected promotions will be linked to the same stack.
            </Typography>
          </Paper>
        )}


        {/* Submit */}
        <Grid container style={{ padding: "20px 18px" }} justifyContent="flex-end">
          <Button
            variant="contained"
            className={classes.submitBtn}
            onClick={handleSubmit}
            disabled={saving || loadingPromotions}
            style={{
              minWidth: 130,
              height: 48,
              borderRadius: 10,
              background: "#305698",
              textTransform: "none",
              fontSize: 15,
              fontWeight: 500,
            }}
          >
            {saving ? <CircularProgress size={22} style={{ color: "#FFFFFF" }} /> : "Create Stack"}
          </Button>
        </Grid>

      </div>
    </React.Fragment>
  );
}